/**
 * When an over-the-air update may NOT reload the app.
 *
 * The bug this exists for: turning alerts on during the welcome restarted the
 * welcome. Asking for a permission sends the app inactive while the OS prompt
 * is up; tapping Allow makes it active again; `appStateChange` is exactly the
 * signal `core/liveupdate.js` uses to apply a staged bundle, so the WebView
 * reloaded mid-answer and the person was back on step one with their taps
 * gone. Nothing crashed — the app had simply started again.
 *
 * So a reload waits for a moment when losing the screen costs nothing. The
 * predicate is deliberately a count and a settle window rather than a boolean:
 * two prompts can overlap, and the resume arrives a beat after the hold is
 * released, which without the window is the same race with better manners.
 *
 * No imports, on purpose — both `app-state.js` and `network.js` hold reloads,
 * and a shared flag that reached either of them would be a cycle.
 */

/** How long after the last hold a reload still counts as interrupting it. */
export const SETTLE_MS = 5_000;

let holds = 0;
let releasedAt = 0;

/** Hold reloads until the returned function is called. Idempotent per caller. */
export function holdReloads(now = Date.now()) {
  holds += 1;
  let done = false;
  return (at = Date.now()) => {
    if (done) return;
    done = true;
    holds = Math.max(0, holds - 1);
    releasedAt = Math.max(releasedAt, at || now);
  };
}

/** Run something the app must not be reloaded during. */
export async function withReloadHold(fn) {
  const release = holdReloads();
  try {
    return await fn();
  } finally {
    release();
  }
}

/** Is something in flight that a reload would interrupt? */
export function reloadsHeld(now = Date.now()) {
  return holds > 0 || now - releasedAt < SETTLE_MS;
}

/** Tests only: forget every hold. */
export function resetReloadHolds() {
  holds = 0;
  releasedAt = 0;
}
